import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import api, { apiErr, fmtMoney, fileUrl } from "../lib/api";
import { getCache, setCache, hasCache } from "../lib/swr";
import { downscaleImage } from "../lib/img";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Plus, Loader2, Plane, Home, Bus, Utensils, Church, FileText, Wifi, Shield, Building2, Handshake, Briefcase, Megaphone, Gift, Receipt, ChevronDown, MoreHorizontal, Trash2, Pencil, ImagePlus, X, Info, Wallet, Lock, HandHeart, Download, FileSpreadsheet, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { tabsListClass, tabTriggerClass } from "../lib/tabStyles";
import { EmptyState } from "../components/EmptyState";
import { SectionBanner } from "../components/SectionBanner";
import GiveDialog from "../components/GiveDialog";
import { openGive } from "../lib/give";

export const CATEGORIES = [
  "Flights", "Housing", "Ground transportation", "Food & fellowship", "Ministry",
  "Visas", "Comms", "Insurance", "Home base", "Local partner", "Office",
  "Advertising", "Donations", "Other",
];

const CAT_ICON = {
  "Flights": Plane, "Housing": Home, "Ground transportation": Bus, "Food & fellowship": Utensils,
  "Ministry": Church, "Visas": FileText, "Comms": Wifi, "Insurance": Shield, "Home base": Building2,
  "Local partner": Handshake, "Office": Briefcase, "Advertising": Megaphone, "Donations": Gift, "Other": Receipt,
};
const catIcon = (c) => CAT_ICON[c] || Receipt;

export default function Budget() {
  const { user, config, refreshConfig, can, canViewFinancials, financialsUpsell, org } = useAuth();
  const isAdmin = user?.role === "admin";
  const canFinance = can ? can("finances") : isAdmin;
  const allowed = canViewFinancials();
  const upsell = financialsUpsell();
  const [giveOpen, setGiveOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "budget" && (can ? can("finances") : user?.role === "admin") ? "budget" : "expenses";
  const [summary, setSummary] = useState(() => getCache("budget:summary") || null);
  const [expenses, setExpenses] = useState(() => getCache("budget:expenses") || []);
  const [items, setItems] = useState(() => getCache("budget:items") || []);
  const [loading, setLoading] = useState(() => !hasCache("budget:summary"));
  const [dialog, setDialog] = useState(null);
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drillCat, setDrillCat] = useState(null);
  const [form, setForm] = useState({ amount: "", currency: "USD", category: "Flights", note: "", country: "", is_public: true, receipt_file_id: null, date: format(new Date(), "yyyy-MM-dd") });

  const load = async () => {
    try {
      const calls = [api.get("/budget/summary"), api.get("/expenses")];
      if (canFinance) calls.push(api.get("/budget/items"));
      const res = await Promise.all(calls);
      setSummary(res[0].data); setExpenses(res[1].data);
      setCache("budget:summary", res[0].data); setCache("budget:expenses", res[1].data);
      if (canFinance) { setItems(res[2].data); setCache("budget:items", res[2].data); }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!allowed) { setLoading(false); return; } load(); }, []);

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingReceipt(true);
    try {
      const compressed = await downscaleImage(file, 1600, 0.85);
      const fd = new FormData(); fd.append("file", compressed); fd.append("purpose", "receipt");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_file_id: data.file_id }));
      toast.success("Receipt attached");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploadingReceipt(false); }
  };

  const openCreate = () => { setForm({ amount: "", currency: summary?.currency || "USD", category: "Flights", note: "", country: "", is_public: true, receipt_file_id: null, date: format(new Date(), "yyyy-MM-dd") }); setDialog({ mode: "create" }); };
  const openEdit = (e) => { setForm({ amount: (e.amount_cents / 100).toString(), currency: e.currency || "USD", category: e.category, note: e.note || "", country: e.country || "", is_public: e.is_public, receipt_file_id: e.receipt_file_id || null, date: e.date || (e.created_at ? format(new Date(e.created_at), "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")) }); setDialog({ mode: "edit", id: e.id }); };

  const saveExpense = async () => {
    if (!form.amount) return toast.error("Enter an amount");
    setSaving(true);
    const payload = { amount_cents: Math.round(parseFloat(form.amount) * 100), currency: form.currency, category: form.category, note: form.note, country: form.country, is_public: form.is_public, receipt_file_id: form.receipt_file_id, date: form.date || null };
    try {
      if (dialog.mode === "edit") await api.put(`/expenses/${dialog.id}`, payload);
      else await api.post("/expenses", payload);
      setDialog(null); toast.success("Saved"); load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const delExpense = async (id) => { try { await api.delete(`/expenses/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  const downloadReport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/budget/export", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "financial-report.xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Financial report downloaded");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setExporting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  if (!allowed) return <FinancesLocked upsell={upsell} giveOpen={giveOpen} setGiveOpen={setGiveOpen} config={config} org={org} />;

  const cur = summary?.currency || "USD";
  const goal = summary?.goal_cents || 0;
  const raised = summary?.raised_cents || 0;
  const spent = summary?.spent_cents || 0;
  const spentPct = goal ? Math.min(100, (spent / goal) * 100) : 0;
  const tankPct = goal ? Math.max(0, Math.min(100 - spentPct, ((raised - spent) / goal) * 100)) : 0;
  const stillPct = Math.max(0, 100 - spentPct - tankPct);
  const byCat = Object.entries(summary?.by_category || {}).sort((a, b) => b[1] - a[1]);
  const maxCat = byCat.length ? byCat[0][1] : 1;

  const plannedByCat = {};
  items.forEach((i) => { plannedByCat[i.category] = (plannedByCat[i.category] || 0) + (i.planned_cents || 0); });
  const spentByCat = {};
  (expenses || []).forEach((e) => { spentByCat[e.category] = (spentByCat[e.category] || 0) + (e.amount_cents || 0); });
  const budgetCats = Array.from(new Set([...Object.keys(spentByCat), ...Object.keys(plannedByCat)]))
    .map((name) => ({ name, spent: spentByCat[name] || 0, planned: plannedByCat[name] || 0 }))
    .sort((a, b) => (b.planned || b.spent) - (a.planned || a.spent));

  const expDate = (e) => e.date ? new Date(e.date + "T00:00:00") : (e.created_at ? new Date(e.created_at) : null);
  const groups = {};
  [...expenses].sort((a, b) => (expDate(b)?.getTime() || 0) - (expDate(a)?.getTime() || 0)).forEach((e) => {
    const dt = expDate(e);
    const key = dt ? format(dt, "MMMM yyyy") : "Undated";
    (groups[key] = groups[key] || []).push(e);
  });

  return (
    <div>
      <SectionBanner title="Stewardship" subtitle="Every gift, transparently tracked" icon={Handshake} accent="#34D399">
        <span className="text-[11px] uppercase tracking-widest text-white/60 font-semibold shrink-0 flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> Live
        </span>
      </SectionBanner>

      <Tabs defaultValue={initialTab}>
        <TabsList className={tabsListClass(isAdmin ? 2 : 1)}>
          <TabsTrigger value="expenses" className={tabTriggerClass} data-testid="tab-expenses"><Receipt className="h-4 w-4" /> Expenses</TabsTrigger>
          {isAdmin && <TabsTrigger value="budget" className={tabTriggerClass} data-testid="tab-budget"><Wallet className="h-4 w-4" /> Budget</TabsTrigger>}
        </TabsList>

        <TabsContent value="expenses">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Stat label="Raised" value={fmtMoney(raised, cur)} />
            <Stat label="Spent" value={fmtMoney(spent, cur)} />
            <Stat label="Remaining" value={fmtMoney(raised - spent, cur)} />
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] mb-6" data-testid="budget-progress-card">
            <div className="h-3 rounded-full bg-muted overflow-hidden flex">
              <div className="h-full transition-[width] duration-1000 ease-out" style={{ width: `${spentPct}%`, background: "var(--tenant-accent)" }} />
              <div className="h-full transition-[width] duration-1000 ease-out" style={{ width: `${tankPct}%`, background: "var(--tenant-accent-light)" }} />
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-muted-foreground">
              <Legend color="var(--tenant-accent)" label={`Spent ${Math.round(spentPct)}%`} />
              <Legend color="var(--tenant-accent-light)" label={`In the tank ${Math.round(tankPct)}%`} />
              <Legend color="#E2E0DA" label={`Still to raise ${Math.round(stillPct)}%`} />
            </div>
          </div>

          {canFinance && (
            <div className="flex flex-wrap gap-2 mb-6">
              <Button data-testid="new-expense-btn" onClick={openCreate} className="rounded-full accent-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Log expense</Button>
              <Button data-testid="export-report-btn" onClick={downloadReport} disabled={exporting} variant="outline" className="rounded-full border-[var(--brand-navy)]/25 text-[var(--brand-navy)] hover:bg-[var(--brand-navy)]/5">
                {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />} Export to Excel
              </Button>
            </div>
          )}

          {canFinance ? (
            budgetCats.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Budget health</h2>
                  <span className="text-[11px] text-muted-foreground">Tap a line for details</span>
                </div>
                <div className="bg-white rounded-2xl p-3 shadow-[0_4px_24px_rgba(44,44,62,0.04)] divide-y divide-[#F0EEE8] mb-6">
                  {budgetCats.map((c) => (
                    <BudgetHealthRow key={c.name} cat={c} cur={cur} onClick={() => setDrillCat(c.name)} />
                  ))}
                </div>
              </>
            )
          ) : (
            byCat.length > 0 && (
              <>
                <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Where it went</h2>
                <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] space-y-4 mb-6">
                  {byCat.map(([name, amt]) => {
                    const Icon = catIcon(name);
                    return (
                      <div key={name} className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-muted-foreground shrink-0" />
                        <div className="flex-1">
                          <div className="flex justify-between text-sm mb-1"><span className="font-medium text-[#2C2C3E]">{name}</span><span className="font-semibold">{fmtMoney(amt, cur)}</span></div>
                          <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full accent-bg" style={{ width: `${(amt / maxCat) * 100}%` }} /></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          )}

          <h2 className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Recent</h2>
          {Object.keys(groups).length === 0 && (
            <EmptyState
              testid="expenses-empty"
              icon={Receipt}
              title="No expenses logged yet"
              description={canFinance ? "Track spending transparently — log your first expense so partners can see where their giving goes." : "As the team logs expenses, they'll appear here for full transparency."}
              actionLabel={canFinance ? "Log an expense" : undefined}
              onAction={canFinance ? openCreate : undefined}
            />
          )}
          {Object.entries(groups).map(([month, list]) => {
            const total = list.reduce((s, e) => s + (e.amount_cents || 0), 0);
            const isCollapsed = collapsed[month];
            return (
              <div key={month} className="mb-3">
                <button data-testid="month-toggle" onClick={() => setCollapsed({ ...collapsed, [month]: !isCollapsed })} className="w-full flex items-center justify-between py-2">
                  <span className="flex items-center gap-2 font-heading font-bold text-[#2C2C3E]"><ChevronDown className={`h-4 w-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} /> {month}</span>
                  <span className="text-sm text-muted-foreground">{list.length} · {fmtMoney(total, cur)}</span>
                </button>
                {!isCollapsed && (
                  <div className="space-y-2">
                    {list.map((e) => {
                      const Icon = catIcon(e.category);
                      return (
                        <div key={e.id} className="bg-white rounded-xl p-4 shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="expense-item">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="h-9 w-9 rounded-xl bg-[var(--brand-navy)]/8 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-[var(--brand-navy)]" /></div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-heading font-bold text-xl text-[#2C2C3E]">{fmtMoney(e.amount_cents, e.currency || cur)}</span>
                                  <span className="text-xs font-semibold text-[var(--brand-navy)] bg-[var(--brand-navy)]/10 px-2 py-0.5 rounded-full">{e.category}</span>
                                  {!e.is_public && <span className="text-[10px] text-muted-foreground">private</span>}
                                </div>
                                {(e.note || e.country) && <div className="text-sm text-[#2C2C3E]/80 mt-1">{[e.note, e.country].filter(Boolean).join(" · ")}</div>}
                                {e.receipt_file_id && (
                                  <a href={fileUrl(`/api/files/${e.receipt_file_id}`)} target="_blank" rel="noreferrer" data-testid="expense-receipt-thumb" className="inline-block mt-2">
                                    <img src={fileUrl(`/api/files/${e.receipt_file_id}`, 200)} alt="receipt" className="h-12 w-12 rounded-lg object-cover border hover:opacity-80 transition-opacity" />
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground">{expDate(e) ? format(expDate(e), "MMM d") : ""}</span>
                              {canFinance && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild><button data-testid="expense-menu-btn" className="text-muted-foreground hover:text-[var(--brand-navy)]"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="rounded-xl">
                                    <DropdownMenuItem data-testid="expense-edit-action" onClick={() => openEdit(e)}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                                    <DropdownMenuItem data-testid="expense-delete-action" onClick={() => delExpense(e.id)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {isAdmin && (
          <TabsContent value="budget">
            <BudgetSetup config={config} refreshConfig={refreshConfig} items={items} expenses={expenses} reload={load} cur={cur} goal={goal} raised={raised} onDrill={setDrillCat} />
          </TabsContent>
        )}
      </Tabs>

      <CategoryDrillDialog
        category={drillCat}
        onClose={() => setDrillCat(null)}
        expenses={expenses}
        planned={drillCat ? (plannedByCat[drillCat] || 0) : 0}
        cur={cur}
      />

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{dialog?.mode === "edit" ? "Edit expense" : "Log expense"}</DialogTitle><DialogDescription className="sr-only">Expense form</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Amount</Label><Input data-testid="expense-amount-input" type="number" className="rounded-xl h-11 mt-1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><Label className="text-sm">Currency</Label><Input data-testid="expense-currency-input" className="rounded-xl h-11 mt-1" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
            </div>
            <div><Label className="text-sm">Date</Label><Input data-testid="expense-date-input" type="date" max={format(new Date(), "yyyy-MM-dd")} className="rounded-xl h-11 mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div>
              <Label className="text-sm">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="expense-category-select" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Country / route</Label><Input data-testid="expense-country-input" className="rounded-xl h-11 mt-1" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
            <Textarea data-testid="expense-note-input" placeholder="Note (optional)" className="rounded-xl" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <div className="flex items-center justify-between"><Label className="text-sm">Show publicly to partners</Label><Switch data-testid="expense-public-switch" checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} /></div>
            <div>
              <Label className="text-sm">Receipt (optional)</Label>
              {form.receipt_file_id ? (
                <div className="mt-1.5 flex items-center gap-3">
                  <img src={fileUrl(`/api/files/${form.receipt_file_id}`, 400)} alt="receipt" className="h-16 w-16 rounded-xl object-cover border" data-testid="expense-receipt-preview" />
                  <button type="button" onClick={() => setForm({ ...form, receipt_file_id: null })} data-testid="expense-receipt-remove" className="flex items-center gap-1 text-sm text-destructive hover:underline"><X className="h-4 w-4" /> Remove</button>
                </div>
              ) : (
                <label className="mt-1.5 flex items-center gap-2 cursor-pointer text-sm rounded-xl border border-dashed p-3 hover:bg-muted/40 transition-colors" data-testid="expense-receipt-label">
                  {uploadingReceipt ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 accent-text" />} Attach receipt image
                  <input type="file" accept="image/*" className="hidden" onChange={uploadReceipt} data-testid="expense-receipt-input" />
                </label>
              )}
            </div>
            <Button data-testid="expense-submit-btn" disabled={saving} onClick={saveExpense} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-[0_4px_24px_rgba(44,44,62,0.04)]">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-heading font-bold text-lg text-[var(--brand-navy)] mt-0.5">{value}</div>
    </div>
  );
}
function Legend({ color, label }) {
  return <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />{label}</span>;
}

function budgetTone(spent, planned) {
  if (!planned) return { color: "#9A96A0", pct: 0, state: "none" };
  const pct = (spent / planned) * 100;
  if (spent > planned) return { color: "#C0392B", pct: 100, state: "over" };
  if (pct >= 80) return { color: "#D08B00", pct, state: "near" };
  return { color: "#1E8A5B", pct, state: "ok" };
}

function BudgetHealthRow({ cat, cur, onClick }) {
  const Icon = catIcon(cat.name);
  const { color, pct, state } = budgetTone(cat.spent, cat.planned);
  const remaining = cat.planned - cat.spent;
  const over = state === "over";
  return (
    <button type="button" onClick={onClick} data-testid="budget-health-row" className="w-full text-left py-3.5 px-2 flex items-center gap-3 hover:bg-muted/30 transition-colors rounded-xl">
      <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1A` }}>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium text-[#2C2C3E] truncate">{cat.name}</span>
          <span className="text-sm shrink-0 tabular-nums">
            <span className="font-bold" style={{ color: over ? "#C0392B" : "#2C2C3E" }}>{fmtMoney(cat.spent, cur)}</span>
            <span className="text-muted-foreground">{cat.planned ? ` / ${fmtMoney(cat.planned, cur)}` : ""}</span>
          </span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden mt-1.5">
          <div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: color }} />
        </div>
        <div className="flex items-center gap-1.5 mt-1 text-xs font-medium" style={{ color }}>
          {state === "none" ? (
            <span className="text-muted-foreground">No budget set — {fmtMoney(cat.spent, cur)} spent</span>
          ) : over ? (
            <><AlertTriangle className="h-3.5 w-3.5" /> Over budget by {fmtMoney(cat.spent - cat.planned, cur)}</>
          ) : state === "near" ? (
            <><AlertTriangle className="h-3.5 w-3.5" /> {Math.round(pct)}% used · {fmtMoney(remaining, cur)} left</>
          ) : (
            <><CheckCircle2 className="h-3.5 w-3.5" /> {Math.round(pct)}% used · {fmtMoney(remaining, cur)} left</>
          )}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

function CategoryDrillDialog({ category, onClose, expenses, planned, cur }) {
  const list = (expenses || []).filter((e) => e.category === category)
    .sort((a, b) => {
      const da = a.date || a.created_at || ""; const dbb = b.date || b.created_at || "";
      return dbb.localeCompare(da);
    });
  const spent = list.reduce((s, e) => s + (e.amount_cents || 0), 0);
  const { color, pct, state } = budgetTone(spent, planned);
  const remaining = planned - spent;
  const Icon = category ? catIcon(category) : Receipt;
  return (
    <Dialog open={!!category} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-3xl max-w-md p-0 overflow-hidden" data-testid="category-drill-dialog">
        <DialogHeader className="p-5 pb-4" style={{ background: `${color}12` }}>
          <DialogTitle className="flex items-center gap-2.5 font-heading text-[var(--brand-navy)]">
            <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ background: `${color}22` }}><Icon className="h-4 w-4" style={{ color }} /></span>
            {category}
          </DialogTitle>
          <DialogDescription className="sr-only">Expenses in {category}</DialogDescription>
          <div className="grid grid-cols-3 gap-2 mt-3">
            <div className="rounded-xl bg-white/70 p-2.5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Planned</div><div className="font-heading font-bold text-[var(--brand-navy)]">{planned ? fmtMoney(planned, cur) : "—"}</div></div>
            <div className="rounded-xl bg-white/70 p-2.5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Spent</div><div className="font-heading font-bold" style={{ color: state === "over" ? "#C0392B" : "var(--brand-navy)" }}>{fmtMoney(spent, cur)}</div></div>
            <div className="rounded-xl bg-white/70 p-2.5"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">{remaining < 0 ? "Over by" : "Left"}</div><div className="font-heading font-bold" style={{ color }}>{fmtMoney(Math.abs(remaining), cur)}</div></div>
          </div>
          {planned > 0 && (
            <div className="mt-2">
              <div className="h-2 rounded-full bg-white/60 overflow-hidden"><div className="h-full" style={{ width: `${pct}%`, background: color }} /></div>
              <div className="text-xs font-medium mt-1 flex items-center gap-1.5" style={{ color }}>
                {state === "over" ? <><AlertTriangle className="h-3.5 w-3.5" /> Over budget</> : state === "near" ? <><AlertTriangle className="h-3.5 w-3.5" /> {Math.round(pct)}% used — getting close</> : <><CheckCircle2 className="h-3.5 w-3.5" /> {Math.round(pct)}% of budget used</>}
              </div>
            </div>
          )}
        </DialogHeader>
        <div className="px-5 pb-5 max-h-[50vh] overflow-y-auto space-y-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{list.length} {list.length === 1 ? "expense" : "expenses"}</div>
          {list.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Nothing logged in this category yet.</p>}
          {list.map((e) => (
            <div key={e.id} className="rounded-xl border border-[#F0EEE8] p-3 flex items-start justify-between gap-3" data-testid="drill-expense-item">
              <div className="min-w-0">
                <div className="font-heading font-bold text-[#2C2C3E]">{fmtMoney(e.amount_cents, e.currency || cur)}{!e.is_public && <span className="ml-2 text-[10px] text-muted-foreground font-sans font-normal">private</span>}</div>
                {(e.note || e.country) && <div className="text-sm text-[#2C2C3E]/75 mt-0.5 truncate">{[e.note, e.country].filter(Boolean).join(" · ")}</div>}
                <div className="text-xs text-muted-foreground mt-0.5">{(e.date || e.created_at) ? format(new Date((e.date ? e.date + "T00:00:00" : e.created_at)), "MMM d, yyyy") : ""}</div>
              </div>
              {e.receipt_file_id && (
                <a href={fileUrl(`/api/files/${e.receipt_file_id}`)} target="_blank" rel="noreferrer" className="shrink-0"><img src={fileUrl(`/api/files/${e.receipt_file_id}`, 200)} alt="receipt" className="h-11 w-11 rounded-lg object-cover border" /></a>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FinancesLocked({ upsell, giveOpen, setGiveOpen, config, org }) {
  return (
    <div data-testid="finances-locked">
      <SectionBanner title="Stewardship" subtitle="Every gift, transparently tracked" icon={Handshake} accent="#34D399" />
      <div className="bg-white rounded-3xl p-7 mt-3 text-center shadow-[0_8px_32px_rgba(44,44,62,0.06)]">
        <div className="h-16 w-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "var(--tenant-accent)" }}>
          {upsell ? <HandHeart className="h-8 w-8 text-white" /> : <Lock className="h-8 w-8 text-white" />}
        </div>
        {upsell ? (
          <>
            <h2 className="font-heading font-extrabold text-2xl text-[var(--brand-navy)] mt-4">Become a financial partner</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
              Full financial transparency — the goal, every gift and where it goes — is shared with our financial partners. Join them, and this whole view opens up to you.
            </p>
            <button data-testid="finances-unlock-give-btn" onClick={() => { const u = config?.donate_url || (org?.slug === "reset" ? "https://akop.live/give" : ""); if (u) openGive(u); else setGiveOpen(true); }}
              className="mt-5 inline-flex items-center gap-2 rounded-full px-6 h-12 text-white font-semibold transition-transform hover:scale-[1.03]" style={{ background: "var(--tenant-accent)" }}>
              <HandHeart className="h-4 w-4" /> Give & unlock transparency
            </button>
            <p className="text-[11px] text-muted-foreground mt-3">Already gave? Ask your mission lead to mark you as a financial partner.</p>
            <GiveDialog open={giveOpen} onOpenChange={setGiveOpen} donateUrl={config?.donate_url} donateLabel={config?.donate_label} orgSlug={org?.slug} />
          </>
        ) : (
          <>
            <h2 className="font-heading font-extrabold text-2xl text-[var(--brand-navy)] mt-4">Kept private</h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
              Financial details for this mission aren't shared here. Thank you for walking with us in prayer and encouragement.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function BudgetSetup({ config, refreshConfig, items, expenses, reload, cur, goal, raised, onDrill }) {
  const [goalInput, setGoalInput] = useState(goal ? (goal / 100).toString() : "");
  const [currency, setCurrency] = useState(cur);
  const [savingGoal, setSavingGoal] = useState(false);
  const [dialog, setDialog] = useState(null); // {mode, item}
  const [f, setF] = useState({ category: "Flights", amount: "", note: "" });
  const [saving, setSaving] = useState(false);

  const spentByCat = {};
  expenses.forEach((e) => { spentByCat[e.category] = (spentByCat[e.category] || 0) + (e.amount_cents || 0); });

  const totalPlanned = items.reduce((s, i) => s + (i.planned_cents || 0), 0);
  const totalSpent = expenses.reduce((s, e) => s + (e.amount_cents || 0), 0);

  const saveGoal = async () => {
    setSavingGoal(true);
    try {
      await api.put("/config", { budget_goal_cents: goalInput ? Math.round(parseFloat(goalInput) * 100) : 0, currency });
      await refreshConfig(); toast.success("Fundraising goal saved"); reload();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSavingGoal(false); }
  };

  const openCreate = () => { setF({ category: "Flights", amount: "", note: "" }); setDialog({ mode: "create" }); };
  const openEdit = (i) => { setF({ category: i.category, amount: (i.planned_cents / 100).toString(), note: i.note || "" }); setDialog({ mode: "edit", item: i }); };
  const saveLine = async () => {
    if (!f.amount) return toast.error("Enter a planned amount");
    setSaving(true);
    const payload = { category: f.category, planned_cents: Math.round(parseFloat(f.amount) * 100), note: f.note };
    try {
      if (dialog.mode === "edit") await api.put(`/budget/items/${dialog.item.id}`, payload);
      else await api.post("/budget/items", payload);
      setDialog(null); reload(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const delItem = async (id) => { try { await api.delete(`/budget/items/${id}`); reload(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-5">
      {(!goal || items.length === 0) && (
        <div className="flex items-start gap-3 rounded-2xl bg-[var(--tenant-accent)]/10 border border-[var(--tenant-accent)]/25 p-4" data-testid="budget-first-hint">
          <Info className="h-5 w-5 accent-text shrink-0 mt-0.5" />
          <p className="text-sm text-[#2C2C3E]">
            <span className="font-semibold">Start here first.</span> Set your fundraising goal and plan your budget categories below — then head to the <span className="font-semibold">Expenses</span> tab to log spending as it happens. This keeps your "raised vs. spent" numbers accurate for partners.
          </p>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Planned" value={fmtMoney(totalPlanned, cur)} />
        <Stat label="Raised" value={fmtMoney(raised || 0, cur)} />
        <Stat label="Spent" value={fmtMoney(totalSpent, cur)} />
      </div>

      <Button data-testid="budget-add-line-btn" onClick={openCreate} className="rounded-full accent-bg text-white hover:opacity-90"><Plus className="h-4 w-4 mr-1" /> Add category</Button>

      {items.length === 0 && <p className="text-sm text-muted-foreground">No planned categories yet. Add one to track spending against a budget.</p>}

      <div className="space-y-3">
        {items.map((i) => {
          const Icon = catIcon(i.category);
          const sp = spentByCat[i.category] || 0;
          const pct = i.planned_cents ? Math.min(100, Math.round((sp / i.planned_cents) * 100)) : 0;
          const over = sp > i.planned_cents;
          return (
            <div key={i.id} onClick={() => onDrill && onDrill(i.category)} className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] cursor-pointer hover:shadow-[0_6px_28px_rgba(44,44,62,0.08)] transition-shadow" data-testid="budget-item">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-[var(--brand-navy)]/8 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 text-[var(--brand-navy)]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-heading font-bold text-lg text-[#2C2C3E]">{i.category}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-sm"><span className={`font-bold ${over ? "text-destructive" : "accent-text"}`}>{fmtMoney(sp, cur)}</span><span className="text-muted-foreground"> / {fmtMoney(i.planned_cents, cur)}</span></span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><button data-testid="budget-item-menu-btn" onClick={(ev) => ev.stopPropagation()} className="text-muted-foreground hover:text-[var(--brand-navy)]"><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="rounded-xl">
                          <DropdownMenuItem data-testid="budget-item-edit-action" onClick={(ev) => { ev.stopPropagation(); openEdit(i); }}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                          <DropdownMenuItem data-testid="budget-item-delete-action" onClick={(ev) => { ev.stopPropagation(); delItem(i.id); }} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden mt-2"><div className="h-full transition-[width] duration-700" style={{ width: `${pct}%`, background: over ? "hsl(var(--destructive))" : "var(--tenant-accent)" }} /></div>
                  <div className="text-xs text-muted-foreground mt-1.5 flex items-center justify-between"><span>{pct}% spent{i.note ? ` · ${i.note}` : ""}</span><span className="flex items-center gap-0.5 accent-text font-medium">View expenses <ChevronRight className="h-3 w-3" /></span></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] space-y-4">
        <h2 className="font-heading font-bold text-lg text-[#2C2C3E]">Fundraising goal</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-sm">Goal amount</Label><Input data-testid="budget-goal-input" type="number" className="rounded-xl h-11 mt-1" value={goalInput} onChange={(e) => setGoalInput(e.target.value)} /></div>
          <div><Label className="text-sm">Currency</Label><Input data-testid="budget-currency-input" className="rounded-xl h-11 mt-1" value={currency} onChange={(e) => setCurrency(e.target.value)} /></div>
        </div>
        <Button data-testid="budget-goal-save-btn" disabled={savingGoal} onClick={saveGoal} className="rounded-full accent-bg text-white hover:opacity-90">{savingGoal ? "Saving…" : "Save goal"}</Button>
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{dialog?.mode === "edit" ? "Edit budget line" : "Add budget category"}</DialogTitle><DialogDescription className="sr-only">Planned budget line</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-sm">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="budget-item-category-select" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-sm">Planned amount</Label><Input data-testid="budget-item-amount-input" type="number" className="rounded-xl h-11 mt-1" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
            <Textarea data-testid="budget-item-note-input" placeholder="Note (optional) — e.g. MCO → Moldova purchased ($700)…" className="rounded-xl" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
            <Button data-testid="budget-item-save-btn" disabled={saving} onClick={saveLine} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
